import type { FC, ReactNode } from 'react';
import React, { useCallback, useMemo } from 'react';
import type { WebsiteHeaderLayout_PhotoBackground } from '@shared/types/WebsiteHeaderSettings.types';
import { Box, Flex, styled } from '@withjoy/joykit';
import { addRendition } from '@shared/utils/photoRendition';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { PHOTO_PRESENTATION_CONFIG } from '@shared/utils/photoPresentationLayout';
import { SsrLazyScreen } from '@apps/guest/packages/layout-engine/components/SsrLazyScreen';
import { EventNameNavForStandaloneHeader } from '../components/EventNameNavForStandaloneHeader/EventNameNavForStandaloneHeader';
import type { EventNameNavForStandaloneHeaderProps } from '../components/EventNameNavForStandaloneHeader/EventNameNavForStandaloneHeader';
import type { EventPageType } from '@graphql/generated-base';
import { BrannanNavigation } from '@graphql/generated-base';
import { pxToRem } from '@withjoy/joykit/theme';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { GuestSiteEventDesign } from '../../../../layout.types';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { BouncingChevron } from '../components/BouncingChevron/BouncingChevron';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { useTranslation } from '@shared/core';
import type { HorizontalAlignment } from '@shared/types/WebsiteHeaderSettings.types';
import { isInIframe } from '@shared/utils/isInIframe';
import { OptimizedEventGuestSitePhoto, LOW_RES_RENDITION } from '@apps/guest/packages/layout-engine/components/OptimizedEventGuestSitePhoto';
import { PhotoBackgroundRenderer } from './PhotoBackgroundRenderer';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';

export type PhotoBackgroundLayoutProps = Readonly<
  {
    websiteHeader: WebsiteHeaderLayout_PhotoBackground;
    showSkeleton?: boolean;
    currentPageType: EventPageType | undefined;
    eventDesign: GuestSiteEventDesign;
    isInlineEditing?: boolean;
    renderedContainerHeight?: number;
    renderedContainerWidth?: number;
    editableContentRef?: React.RefObject<HTMLDivElement>;
    forceRenderViewIn?: 'mobile' | 'desktop';
    scaleValue?: number;
    placeholderImageUrl?: string;
    onTextAlignmentChange?: (alignment: HorizontalAlignment) => void;
  } & EventNameNavForStandaloneHeaderProps
>;

/* Sits above art sprites (docked+1) that live inside FrameOuter, which is a sibling
   rendered after StandaloneWebsiteHeader in the DOM. */
const PhotoBackgroundOuter = styled(Box)`
  z-index: ${({ theme }) => theme.zIndices.docked + 2};
`;

const CONTAINER_ASPECT_RATIO = PHOTO_PRESENTATION_CONFIG.photoBackground.aspectRatio as number;
const CONTAINER_MOBILE_ASPECT_RATIO = PHOTO_PRESENTATION_CONFIG.photoBackground.mobileAspectRatio as number;

const isPreviewing = isInIframe();

export const PhotoBackgroundLayout: FC<PhotoBackgroundLayoutProps> = props => {
  const {
    websiteHeader,
    showSkeleton,
    currentPageType,
    isInlineEditing = false,
    renderedContainerHeight,
    renderedContainerWidth,
    editableContentRef,
    forceRenderViewIn,
    scaleValue = 1,
    placeholderImageUrl,
    onTextAlignmentChange,
    pages,
    pageSlug,
    ...restProps
  } = props;

  const photo = websiteHeader.headerPhoto.photos?.[0];
  const { headerTitleStyle, navigationStyle } = websiteHeader.headerPhoto;

  const [containerDimensions, containerRef] = useSizeObserver();

  const [isMobile = false] = useResponsive({ values: { xxs: true, sm: false } });
  const [isBouncingChevronEnabled] = useIsFeatureEnabled('guestBackgroundHeaderBouncingChevronEnabled');
  const deviceSpecificContainerAspectRatio = isMobile && photo?.mobileCropInfo ? CONTAINER_MOBILE_ASPECT_RATIO : CONTAINER_ASPECT_RATIO;

  const { checkIsWelcomePage } = useWelcomePage();
  const { isWelcomePage } = checkIsWelcomePage(pageSlug, currentPageType);

  const pageIndex = pages?.findIndex(p => p.pageSlug === pageSlug);
  const isCompactView = restProps.eventDesign?.brannanNavigation === BrannanNavigation.classic && !isWelcomePage && pageIndex !== 0;

  const shouldHidePhotoBackground = useMemo(() => {
    return isCompactView && restProps.navBarVariant === 'menu';
  }, [isCompactView, restProps.navBarVariant]);

  const { tq } = useTranslation('inlineEditor');

  const { height, aspectRatio } = useMemo(
    () =>
      isCompactView
        ? { height: '100%', aspectRatio: 'unset' }
        : {
            height: isInlineEditing && renderedContainerHeight ? `${renderedContainerHeight}px` : isMobile && isBouncingChevronEnabled ? '90svh' : '100svh',
            aspectRatio: `${deviceSpecificContainerAspectRatio}`,
          },
    [deviceSpecificContainerAspectRatio, isBouncingChevronEnabled, isCompactView, isInlineEditing, isMobile, renderedContainerHeight],
  );

  const viewportHeightFallback = isMobile && isBouncingChevronEnabled ? '90svh' : '100svh';

  const { containerAspectRatio, containerHeight, containerMinHeight, containerOverflow } = useMemo(() => {
    return {
      containerAspectRatio: shouldHidePhotoBackground ? 'unset' : aspectRatio,
      containerHeight: shouldHidePhotoBackground ? 'auto' : height,
      containerMinHeight: shouldHidePhotoBackground ? 'auto' : pxToRem(96),
      containerOverflow: shouldHidePhotoBackground || isInlineEditing ? 'visible' : 'hidden',
    };
  }, [shouldHidePhotoBackground, aspectRatio, height, isInlineEditing]);

  const handleEditTextClick = useEventCallback(() => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editWebsiteHeaderCustomTitle',
        inlineEditData: { data: props, validationConstraints: { maxLength: 30 } },
      },
    });
  });

  const handleEditPhotoClick = useEventCallback(() => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editHeaderPhoto' },
    });
  });

  const renderPhotoWrapper = useCallback(
    (photoElement: ReactNode) => (
      <InlineEditor
        elementLabel={tq`Photo`}
        wrapperType="actionInside"
        actionData={{ editPhoto: handleEditPhotoClick }}
        componentName="headerPhoto"
        pageName={currentPageType}
        pageSlug={pageSlug || ''}
        wrapperCSS={{ width: '100%', height: '100%' }}
        isEligibleForInlineEditing={!isCompactView}
      >
        {photoElement}
      </InlineEditor>
    ),
    [tq, handleEditPhotoClick, currentPageType, pageSlug, isCompactView],
  );

  const eventNameNavProps = {
    eventDisplayName: websiteHeader.eventDisplayName,
    colors: websiteHeader.headerPhoto.colors,
    headerTextData: websiteHeader.headerPhoto.eventName,
    headerTitleStyle,
    navigationStyle,
    isCompactView,
    currentPageType,
    handleEditTextClick,
    isContentEditable: isInlineEditing,
    editableContentRef,
    forceRenderViewIn,
    scaleValue,
    pages,
    pageSlug,
    renderedContainerHeight,
    renderedContainerWidth,
    viewportHeightFallback,
    onTextAlignmentChange,
    ...restProps,
  };

  return (
    <PhotoBackgroundOuter
      position="relative"
      width="100%"
    >
      <Flex
        ref={containerRef}
        position="relative"
        width="100%"
        aspectRatio={containerAspectRatio}
        height={containerHeight}
        minHeight={containerMinHeight}
        overflow={containerOverflow}
      >
        {shouldHidePhotoBackground ? (
          <EventNameNavForStandaloneHeader {...eventNameNavProps} />
        ) : (
          <SsrLazyScreen
            src={addRendition({ url: photo?.url || placeholderImageUrl, renditionSize: LOW_RES_RENDITION })}
            width="100%"
            height={height}
            aspectRatio={aspectRatio}
            skeletonGroupOverrides={{
              Root: {
                props: {
                  width: '100%',
                  height: height,
                  aspectRatio: aspectRatio,
                },
              },
              PlaceholderRoot: {
                props: {
                  width: '100%',
                  height: height,
                  aspectRatio: aspectRatio,
                  position: 'absolute',
                },
              },
            }}
            showSkeleton={showSkeleton}
          >
            {() => (
              <PhotoBackgroundRenderer
                photo={photo}
                placeholderImageUrl={placeholderImageUrl}
                containerDimensions={containerDimensions}
                height={height}
                isCompactView={isCompactView}
                renderedContainerHeight={renderedContainerHeight}
                renderedContainerWidth={renderedContainerWidth}
                isMobile={isMobile}
                deviceSpecificContainerAspectRatio={deviceSpecificContainerAspectRatio}
                photoRenderer={OptimizedEventGuestSitePhoto}
                renderPhotoWrapper={renderPhotoWrapper}
              >
                <Box
                  width="100%"
                  height="100%"
                  position={isCompactView ? 'relative' : 'absolute'}
                  zIndex="banner"
                  pointerEvents={isPreviewing ? 'none' : 'auto'}
                >
                  <EventNameNavForStandaloneHeader {...eventNameNavProps} />
                </Box>
              </PhotoBackgroundRenderer>
            )}
          </SsrLazyScreen>
        )}
      </Flex>
      {isBouncingChevronEnabled && isMobile && !isCompactView && !shouldHidePhotoBackground && !isInlineEditing && <BouncingChevron />}
    </PhotoBackgroundOuter>
  );
};

PhotoBackgroundLayout.displayName = 'PhotoBackgroundLayout';
